import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../../components/common/Button';
import './AdminPages.css';

const AdminQuestionForm = ({ question, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    text: { en: '', mr: '' },
    helpText: { en: '', mr: '' },
    pillar: 'LEAD',
    type: 'scored',
    weight: 1,
    maxScore: 5,
    orderIndex: 1,
    active: true,
    bonusChoices: ['']
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (question) {
      setFormData({
        text: question.text || { en: '', mr: '' },
        helpText: question.helpText || { en: '', mr: '' },
        pillar: question.pillar || 'LEAD',
        type: question.type || 'scored',
        weight: question.weight || 1,
        maxScore: question.maxScore || 5,
        orderIndex: question.orderIndex || 1,
        active: question.active !== undefined ? question.active : true,
        bonusChoices: question.bonusChoices || ['']
      });
    }
  }, [question]);

  const validate = () => {
    const newErrors = {};

    if (!formData.text.en.trim()) {
      newErrors.textEn = 'English text is required';
    }

    if (formData.weight <= 0) {
      newErrors.weight = 'Weight must be positive';
    }

    if (formData.maxScore < 0) {
      newErrors.maxScore = 'Max score cannot be negative';
    }

    if (formData.orderIndex < 0) {
      newErrors.orderIndex = 'Order index cannot be negative';
    }

    if (formData.type === 'bonus' && formData.bonusChoices.filter(c => c.trim()).length < 2) {
      newErrors.bonusChoices = 'Bonus questions need at least 2 choices';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    try {
      setSubmitting(true);
      
      const dataToSubmit = {
        ...formData,
        bonusChoices: formData.type === 'bonus' 
          ? formData.bonusChoices.filter(c => c.trim()) 
          : []
      };

      await onSubmit(dataToSubmit);
    } catch (error) {
      alert('Error saving question: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const addBonusChoice = () => {
    setFormData({
      ...formData,
      bonusChoices: [...formData.bonusChoices, '']
    });
  };

  const removeBonusChoice = (index) => {
    setFormData({
      ...formData,
      bonusChoices: formData.bonusChoices.filter((_, i) => i !== index)
    });
  };

  const updateBonusChoice = (index, value) => {
    const newChoices = [...formData.bonusChoices];
    newChoices[index] = value;
    setFormData({
      ...formData,
      bonusChoices: newChoices
    });
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>{question ? 'Edit Question' : 'Add New Question'}</h1>
          <p>Configure question details and translations</p>
        </div>
        <Button variant="secondary" onClick={onCancel}>
          ← Back to Questions
        </Button>
      </div>

      <motion.div
        className="admin-form-card"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <form onSubmit={handleSubmit}>
          {/* Question Text - English */}
          <div className="form-group">
            <label>Question Text (English) *</label>
            <textarea
              value={formData.text.en}
              onChange={(e) => setFormData({
                ...formData,
                text: { ...formData.text, en: e.target.value }
              })}
              rows="3"
              required
              placeholder="Enter question text in English"
            />
            {errors.textEn && <span className="error-text">{errors.textEn}</span>}
          </div>

          {/* Question Text - Marathi */}
          <div className="form-group">
            <label>Question Text (Marathi)</label>
            <textarea
              value={formData.text.mr}
              onChange={(e) => setFormData({
                ...formData,
                text: { ...formData.text, mr: e.target.value }
              })}
              rows="3"
              placeholder="प्रश्न मराठीत प्रविष्ट करा"
            />
          </div>

          {/* Help Text - English */}
          <div className="form-group">
            <label>Help Text (English)</label>
            <textarea
              value={formData.helpText.en}
              onChange={(e) => setFormData({
                ...formData,
                helpText: { ...formData.helpText, en: e.target.value }
              })}
              rows="2"
              placeholder="Optional tooltip text in English"
            />
          </div>

          {/* Help Text - Marathi */}
          <div className="form-group">
            <label>Help Text (Marathi)</label>
            <textarea
              value={formData.helpText.mr}
              onChange={(e) => setFormData({
                ...formData,
                helpText: { ...formData.helpText, mr: e.target.value }
              })}
              rows="2"
              placeholder="वैकल्पिक टूलटिप मजकूर मराठीत"
            />
          </div>

          {/* Pillar and Type */}
          <div className="form-row">
            <div className="form-group">
              <label>Pillar *</label>
              <select
                value={formData.pillar}
                onChange={(e) => setFormData({ ...formData, pillar: e.target.value })}
                required
              >
                <option value="LEAD">LEAD</option>
                <option value="EVOLVE">EVOLVE</option>
                <option value="ALIGN">ALIGN</option>
                <option value="PERFORM">PERFORM</option>
              </select>
            </div>

            <div className="form-group">
              <label>Type *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
              >
                <option value="scored">Scored</option>
                <option value="bonus">Bonus</option>
              </select>
            </div>
          </div>

          {/* Weight and Max Score */}
          <div className="form-row">
            <div className="form-group">
              <label>Weight *</label>
              <input
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) })}
                min="0.1"
                step="0.1"
                required
              />
              {errors.weight && <span className="error-text">{errors.weight}</span>}
              <small style={{ color: '#666', fontSize: '0.85rem' }}>
                Multiplier for scoring (default: 1)
              </small>
            </div>

            <div className="form-group">
              <label>Max Score *</label>
              <input
                type="number"
                value={formData.maxScore}
                onChange={(e) => setFormData({ ...formData, maxScore: parseInt(e.target.value) })}
                min="0"
                required
              />
              {errors.maxScore && <span className="error-text">{errors.maxScore}</span>}
              <small style={{ color: '#666', fontSize: '0.85rem' }}>
                Maximum points for this question (default: 5)
              </small>
            </div>
          </div>

          {/* Order Index */}
          <div className="form-group">
            <label>Order Index *</label>
            <input
              type="number"
              value={formData.orderIndex}
              onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) })}
              min="0"
              required
            />
            {errors.orderIndex && <span className="error-text">{errors.orderIndex}</span>}
            <small style={{ color: '#666', fontSize: '0.85rem' }}>
              Display order in the assessment (lower numbers appear first)
            </small>
          </div>

          {/* Bonus Choices (only for bonus questions) */}
          {formData.type === 'bonus' && (
            <div className="form-group">
              <label>Bonus Choices *</label>
              {formData.bonusChoices.map((choice, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    value={choice}
                    onChange={(e) => updateBonusChoice(index, e.target.value)}
                    placeholder={`Choice ${index + 1}`}
                    style={{ flex: 1 }}
                  />
                  {formData.bonusChoices.length > 1 && (
                    <button 
                      type="button" 
                      onClick={() => removeBonusChoice(index)} 
                      className="btn-delete" 
                      style={{ padding: '0.5rem 1rem' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button 
                type="button" 
                onClick={addBonusChoice} 
                className="btn-secondary" 
                style={{ marginTop: '0.5rem' }}
              >
                + Add Choice
              </button>
              {errors.bonusChoices && <span className="error-text">{errors.bonusChoices}</span>}
            </div>
          )}

          {/* Active Toggle */}
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
              Active
            </label>
            <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
              Inactive questions are hidden from new assessments but preserved in historical data
            </small>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : (question ? 'Update Question' : 'Create Question')}
            </Button>
            <Button variant="secondary" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminQuestionForm;
